import React from 'react';
import { MobxComponent } from "../../../mobx/components";
import { inject, observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import { IconClose, IconArrowForward, IconAlertDanger, IconAlert, IconAlertDefault, IconArrowRight } from "./icons";
import { MenuItem, QuickSettingsContainer, MenuColorHeaderWrapper, MenuColorHeaderTitleWrapper, MenuColorBodyWrapper, MenuColorFooterWrapper, ButtonV2, MenuStatusAlert, MenuStatusAlertText, MenuStatusParagraph, MenuStatusAssignmentContainer, MenuStatusAssignmentList, MenuStatusLabel, MenuStatusAssignmentTo, MenuStatusShowSettingsContainer, MenuStatusShowSettingsText, MenuBodyFooter, MenuStatusLabelAvailable } from "./layout.c";
import { RotateLoader } from "@lib/components";
import { IconKioskWrapper } from '../../ui/restaurant/views/menus-v2/item-v2';
import { CoreUtils, ListServicesOptionsNoB } from '@lib/common';
import { POS_PRINTERS } from './data';
import { Icon } from '../../ui/restaurant/notifications/list.components';

/** Describes which entity will be updated (menu, category, or item) */
export type OverrideEntityType = 'menu' | 'category' | 'item';

/**
 * What kind of override the user is confirming — each maps to a dedicated modal scope
 * (colors vs printers vs POS/online & service types), not one combined list.
 */
export type OverrideEditKind = 'visibility' | 'color' | 'printers' | 'receipt_printers';

export interface OverrideTargetEntity {
    type: OverrideEntityType;
    name: string;
    conditions?: any;
    categoryCount?: number;
    itemCount?: number;
}

/** Source ids to compute counts and services from store */
export interface OverrideSourceIds {
    menuId: string;
    categoryId?: string;
    itemId?: string;
}

export interface OverrideFieldChange {
    from?: string;
    to?: string;
}

export interface OverrideArrayChange {
    from?: string[];
    to?: string[];
}

/** Summary of what will be overridden at the entity being edited */
export interface OverrideChangesSummary {
    /** Display / UI color */
    displayColor?: OverrideFieldChange;
    /** POS (pushpos) color */
    posColor?: OverrideFieldChange;
    /** @deprecated use displayColor — kept for backward compatibility */
    color?: OverrideFieldChange;
    /** Legacy printer wording in UI */
    printers?: OverrideArrayChange;
    posPrinters?: OverrideArrayChange;
    serviceTypes?: OverrideArrayChange;
    /** POS/online enabled + service labels (human-readable); only when different from before. */
    posOnlinePreview?: OverrideFieldChange;
}

/** One descendant row shown in the modal */
export interface AffectedEntityRow {
    entityType: 'category' | 'item';
    name: string;
    id: string;
    displayColor?: OverrideFieldChange;
    posColor?: OverrideFieldChange;
    /** Legacy printer wording */
    printers?: OverrideArrayChange;
    posPrinters?: OverrideArrayChange;
    serviceTypes?: OverrideArrayChange;
    visibilityPreview?: OverrideFieldChange;
}

type Step = 1 | 2;

/**
 * Collects POS + online service value ids from an availability row.
 * Supports both API shapes: `{ conditions: { pos, online } }` and flat `{ pos, online }` (e.g. after MenuVisibility merge).
 */
export function getServicesFromAvailability(avail: any): string[] {
    if (avail == null || typeof avail !== 'object') return [];
    const c =
        avail.conditions != null && typeof avail.conditions === 'object'
            ? avail.conditions
            : avail;
    const pos = c.pos?.services ?? [];
    const online = c.online?.services ?? [];
    const p = Array.isArray(pos) ? pos : [];
    const o = Array.isArray(online) ? online : [];
    return [...new Set([...p, ...o])];
}

export function getServiceLabels(serviceValues: string[]): string[] {
    return serviceValues
        .map(v => ListServicesOptionsNoB.find(o => o.value === v)?.label)
        .filter(Boolean) as string[];
}

/** Resolves `pos` / `online` from nested `conditions` or flat availability rows. */
export function getPosOnlineSlice(avail: any): { pos?: any; online?: any } {
    if (avail == null || typeof avail !== 'object') return {};
    const c =
        avail.conditions != null && typeof avail.conditions === 'object'
            ? avail.conditions
            : avail;
    return { pos: c.pos, online: c.online };
}

function posOnlineChannelEnabled(block: any): boolean {
    if (block == null || typeof block !== 'object') return false;
    if (Object.keys(block).length === 0) return false;
    return !!block.enabled;
}

/** True when the resolved availability has POS channel enabled (menu list filtering). */
export function isAvailabilityPosEnabled(avail: any): boolean {
    return posOnlineChannelEnabled(getPosOnlineSlice(avail).pos);
}

/** True when the resolved availability has Online channel enabled (menu list filtering). */
export function isAvailabilityOnlineEnabled(avail: any): boolean {
    return posOnlineChannelEnabled(getPosOnlineSlice(avail).online);
}

function allServiceValuesSelected(services: string[] | undefined): boolean {
    const all = ListServicesOptionsNoB.map(o => o.value);
    if (!all.length || !services?.length) return false;
    const set = new Set(services);
    return all.every(v => set.has(v));
}

/**
 * Single-line summary for POS / online: enabled state + services ("POS only: Pickup, Delivery", "POS & Online: All services", …).
 */
export function formatPosOnlineAvailabilityPreview(avail: any): string {
    const { pos, online } = getPosOnlineSlice(avail);
    const posOn = posOnlineChannelEnabled(pos);
    const onlineOn = posOnlineChannelEnabled(online);
    const posSvcs = Array.isArray(pos?.services) ? pos.services : [];
    const onlineSvcs = Array.isArray(online?.services) ? online.services : [];
    const posLabels = getServiceLabels(posSvcs);
    const onlineLabels = getServiceLabels(onlineSvcs);
    const posAll = allServiceValuesSelected(posSvcs);
    const onlineAll = allServiceValuesSelected(onlineSvcs);

    if (!posOn && !onlineOn) {
        return '—';
    }
    if (posOn && !onlineOn) {
        const inner = posAll ? 'All services' : posLabels.length ? posLabels.join(', ') : 'On';
        return `POS only: ${inner}`;
    }
    if (!posOn && onlineOn) {
        const inner = onlineAll ? 'All services' : onlineLabels.length ? onlineLabels.join(', ') : 'On';
        return `Online only: ${inner}`;
    }
    if (posAll && onlineAll) {
        return 'POS & Online: All services';
    }
    const parts: string[] = [];
    parts.push(`POS: ${posAll ? 'All services' : posLabels.length ? posLabels.join(', ') : 'On'}`);
    parts.push(`Online: ${onlineAll ? 'All services' : onlineLabels.length ? onlineLabels.join(', ') : 'On'}`);
    return parts.join(' · ');
}

function normalizePosOnlineForCompare(avail: any): string {
    const { pos, online } = getPosOnlineSlice(avail);
    const pe = posOnlineChannelEnabled(pos);
    const oe = posOnlineChannelEnabled(online);
    const ps = [...(pos?.services ?? [])].filter(Boolean).sort().join('\0');
    const os = [...(online?.services ?? [])].filter(Boolean).sort().join('\0');
    return JSON.stringify({ pe, oe, ps, os });
}

/** True when POS/online enabled flags or service selections differ from previous snapshot. */
export function posOnlineVisibilityChanged(before: any, after: any): boolean {
    return normalizePosOnlineForCompare(before) !== normalizePosOnlineForCompare(after);
}

/** Map stored pushpos printer slot values (1–8, etc.) to labels from {@link POS_PRINTERS}. */
/** Show override colors as #hex; pass through non-rgb strings (e.g. "—", named colors). */
export function displayColorAsHex(value: string | undefined | null): string {
    if (value == null || value === '') return '';
    const s = String(value).trim();
    if (s === '—') return '—';
    if (s.startsWith('#')) {
        const body = CoreUtils.colors.rgbToHex(s).replace(/^#/, '');
        return `#${body}`;
    }
    if (/^rgba?\(/i.test(s)) {
        const body = CoreUtils.colors.rgbToHex(s).replace(/^#/, '');
        return `#${body}`;
    }
    return s;
}

export function formatPosPrinterValuesForDisplay(values: readonly any[] | undefined): string[] {
    if (!values?.length) return [];
    return values.map((v) => {
        if (typeof v === 'number' && Number.isFinite(v)) {
            const hit = POS_PRINTERS.find(p => p.value === v);
            if (hit) return hit.label;
        }
        if (typeof v === 'string' && v.trim() !== '') {
            const num = Number(v);
            if (!Number.isNaN(num) && Number.isFinite(num)) {
                const hit = POS_PRINTERS.find(p => p.value === num);
                if (hit) return hit.label;
            }
            const byLabel = POS_PRINTERS.find(p => p.label === v);
            if (byLabel) return byLabel.label;
        }
        return String(v);
    });
}

function getCountsAndServices(
    menusV2: any[],
    overrideSource: OverrideSourceIds | undefined,
    entityType: OverrideEntityType
): { categoryCount: number; itemCount: number; serviceLabels: string[] } {
    const empty = { categoryCount: 0, itemCount: 0, serviceLabels: [] as string[] };
    if (!overrideSource?.menuId || !menusV2?.length) return empty;
    const menu = menusV2.find((m: any) => m.id === overrideSource.menuId);
    if (!menu?.availabilities) return empty;
    const getAvail = (id: string | undefined) => id ? menu.availabilities.find((a: any) => a.id === id) : null;
    const collectServices = (avail: any) => getServiceLabels(getServicesFromAvailability(avail));

    if (entityType === 'menu') {
        const categoryCount = menu.categories?.length ?? 0;
        const itemCount = menu.items?.length ?? 0;
        const serviceSet = new Set<string>();
        const menuAvail = getAvail(menu.availability_id);
        getServicesFromAvailability(menuAvail).forEach(s => serviceSet.add(s));
        (menu.categories ?? []).forEach((cat: any) => {
            getServicesFromAvailability(getAvail(cat.availability_id)).forEach(s => serviceSet.add(s));
        });
        (menu.items ?? []).forEach((it: any) => {
            const aid = it.availability_id ?? (menu.categories?.find((c: any) => c.id === it.category_id) as any)?.availability_id ?? menu.availability_id;
            getServicesFromAvailability(getAvail(aid)).forEach(s => serviceSet.add(s));
        });
        return { categoryCount, itemCount, serviceLabels: getServiceLabels([...serviceSet]) };
    }

    if (entityType === 'category' && overrideSource.categoryId) {
        const category = menu.categories?.find((c: any) => c.id === overrideSource.categoryId);
        if (!category) return empty;
        const items = (menu.items ?? []).filter((i: any) => i.category_id === overrideSource.categoryId);
        const itemCount = items.length;
        const serviceSet = new Set<string>();
        getServicesFromAvailability(getAvail(category.availability_id)).forEach(s => serviceSet.add(s));
        items.forEach((it: any) => {
            const aid = it.availability_id ?? category.availability_id ?? menu.availability_id;
            getServicesFromAvailability(getAvail(aid)).forEach(s => serviceSet.add(s));
        });
        return { categoryCount: 0, itemCount, serviceLabels: getServiceLabels([...serviceSet]) };
    }

    if (entityType === 'item' && overrideSource.categoryId && overrideSource.itemId) {
        const item = menu.items?.find((i: any) => i.id === overrideSource.itemId);
        if (!item) return empty;
        const category = menu.categories?.find((c: any) => c.id === overrideSource.categoryId);
        const aid = item.availability_id ?? (category as any)?.availability_id ?? menu.availability_id;
        const serviceLabels = collectServices(getAvail(aid));
        return { categoryCount: 0, itemCount: 1, serviceLabels };
    }

    return empty;
}

interface Props extends WithTranslation {
    show?: boolean;
    onHide?: () => void;
    onConfirm?: () => void;
    onKeepCurrent?: () => void;
    onShowSettings?: () => void;
    title?: string;
    targetEntity?: OverrideTargetEntity | null;
    overrideSource?: OverrideSourceIds | null;
    changesSummary?: OverrideChangesSummary | null;
    /** visibility = service types focus; color = display color focus */
    editKind?: OverrideEditKind;
    /** Descendants that have settings which may be affected */
    affectedEntities?: AffectedEntityRow[] | null;
    /** Category edit with multiple setting types changing at once */
    multiSetting?: boolean;
}

interface State {
    step: Step;
    toggleAffectedItems: boolean;
    isConfirming: boolean;
}

function renderArrowPair(from?: string, to?: string, fromIsColor?: boolean, toIsColor?: boolean) {
    return (
        <MenuStatusAssignmentTo>
            {from != null && from !== '' && (
                <MenuStatusParagraph style={fromIsColor && from !== '—' ? { color: from } : undefined}>{from}</MenuStatusParagraph>
            )}
            {(from != null || to != null) && <IconArrowRight />}
            {to != null && to !== '' && (
                <MenuStatusParagraph style={toIsColor && to !== '—' ? { color: to } : undefined}>{to}</MenuStatusParagraph>
            )}
        </MenuStatusAssignmentTo>
    );
}

/** Swatch uses raw CSS color; labels use {@link displayColorAsHex}. */
function renderColorArrowPair(from?: string, to?: string) {
    const fromLabel = from != null && from !== '' ? displayColorAsHex(from) : undefined;
    const toLabel = to != null && to !== '' ? displayColorAsHex(to) : undefined;
    return (
        <MenuStatusAssignmentTo>
            {fromLabel != null && fromLabel !== '' && (
                <MenuStatusParagraph style={from && from !== '—' ? { color: from } : undefined}>{fromLabel}</MenuStatusParagraph>
            )}
            {(from != null || to != null) && <IconArrowRight />}
            {toLabel != null && toLabel !== '' && (
                <MenuStatusParagraph style={to && to !== '—' ? { color: to } : undefined}>{toLabel}</MenuStatusParagraph>
            )}
        </MenuStatusAssignmentTo>
    );
}

function renderArrowPairLists(from: string[], to: string[]) {
    const fl = from.length ? from.join(', ') : '—';
    const tl = to.length ? to.join(', ') : '—';
    return (
        <MenuStatusAssignmentTo>
            <MenuStatusParagraph>{fl}</MenuStatusParagraph>
            <IconArrowRight />
            <MenuStatusParagraph>{tl}</MenuStatusParagraph>
        </MenuStatusAssignmentTo>
    );
}

function renderPosPrinterArrowPairLists(from: readonly any[] | undefined, to: readonly any[] | undefined) {
    return renderArrowPairLists(
        formatPosPrinterValuesForDisplay(from),
        formatPosPrinterValuesForDisplay(to)
    );
}

function filterAffectedRowsForKind(
    rows: AffectedEntityRow[] | null | undefined,
    kind: OverrideEditKind
): AffectedEntityRow[] {
    if (!rows?.length) return [];
    return rows.map((row) => {
        if (kind === 'color') {
            const next: AffectedEntityRow = { entityType: row.entityType, name: row.name, id: row.id };
            if (row.displayColor) next.displayColor = row.displayColor;
            if (row.posColor) next.posColor = row.posColor;
            return next;
        }
        if (kind === 'visibility') {
            const next: AffectedEntityRow = { entityType: row.entityType, name: row.name, id: row.id };
            if (row.serviceTypes) next.serviceTypes = row.serviceTypes;
            if (row.visibilityPreview) next.visibilityPreview = row.visibilityPreview;
            return next;
        }
        if (kind === 'receipt_printers') {
            const next: AffectedEntityRow = { entityType: row.entityType, name: row.name, id: row.id };
            if (row.printers) next.printers = row.printers;
            return next;
        }
        /* printers */
        const next: AffectedEntityRow = { entityType: row.entityType, name: row.name, id: row.id };
        if (row.posPrinters) next.posPrinters = row.posPrinters;
        if (row.printers) next.printers = row.printers;
        return next;
    }).filter((row) => {
        if (kind === 'color') {
            return !!(row.displayColor || row.posColor);
        }
        if (kind === 'visibility') {
            // Keep fallback descendant rows (name/id only) so "Show Affected Items"
            // still lists entities when we intentionally open override with count-based fallback.
            return true;
        }
        if (kind === 'receipt_printers') {
            return !!row.printers;
        }
        return !!(row.posPrinters || row.printers);
    });
}

function filterChangesSummaryForKind(
    summary: OverrideChangesSummary | null | undefined,
    kind: OverrideEditKind
): OverrideChangesSummary | null {
    if (!summary) return null;
    if (kind === 'color') {
        const o: OverrideChangesSummary = {};
        const disp = summary.displayColor ?? summary.color;
        if (disp && (disp.from !== undefined || disp.to !== undefined)) o.displayColor = disp;
        if (summary.posColor && (summary.posColor.from !== undefined || summary.posColor.to !== undefined)) {
            o.posColor = summary.posColor;
        }
        return Object.keys(o).length ? o : null;
    }
    if (kind === 'visibility') {
        const o: OverrideChangesSummary = {};
        const po = summary.posOnlinePreview;
        if (po && (po.from !== undefined || po.to !== undefined) && po.from !== po.to) {
            o.posOnlinePreview = po;
        }
        if (summary.serviceTypes && (summary.serviceTypes.from?.length || summary.serviceTypes.to?.length)) {
            o.serviceTypes = summary.serviceTypes;
        }
        return Object.keys(o).length ? o : null;
    }
    if (kind === 'receipt_printers') {
        const o: OverrideChangesSummary = {};
        if (summary.printers && (summary.printers.from?.length || summary.printers.to?.length)) {
            o.printers = summary.printers;
        }
        return Object.keys(o).length ? o : null;
    }
    const o: OverrideChangesSummary = {};
    if (summary.printers && (summary.printers.from?.length || summary.printers.to?.length)) {
        o.printers = summary.printers;
    }
    if (summary.posPrinters && (summary.posPrinters.from?.length || summary.posPrinters.to?.length)) {
        o.posPrinters = summary.posPrinters;
    }
    return Object.keys(o).length ? o : null;
}

function parentLevelLabel(entityType: OverrideEntityType | undefined): string {
    return entityType === 'category' ? 'category-level' : 'menu-level';
}

function getStep1AlertText(editKind: OverrideEditKind, targetType: OverrideEntityType | undefined, multiSetting?: boolean): string {
    if (targetType === 'item' || (multiSetting && targetType === 'category')) {
        return 'Item setting will be overridden';
    }
    return 'Item settings will be overridden';
}

function getModalTitleSuffix(editKind: OverrideEditKind, targetType: OverrideEntityType | undefined): string {
    if (editKind === 'color') {
        if (targetType === 'item') return 'Item Color';
        if (targetType === 'category') return 'Category Color';
        return 'Menu Color';
    }
    if (editKind === 'printers' || editKind === 'receipt_printers') {
        return 'Printer Settings';
    }
    return 'Service Type';
}

function getStep1BodyText(args: {
    editKind: OverrideEditKind;
    targetType: OverrideEntityType | undefined;
    affectedCountPhrase: string;
    multiSetting?: boolean;
}): string {
    const { editKind, targetType, affectedCountPhrase, multiSetting } = args;

    if (targetType === 'item') {
        if (editKind === 'color') {
            return 'This item has custom color settings that will be replaced by the menu-level setting.';
        }
        if (editKind === 'visibility') {
            return 'This item currently uses the menu-level setting. A custom setting will be applied instead.';
        }
        if (editKind === 'printers') {
            return 'This item currently uses the menu-level setting. A custom setting will be applied instead.';
        }
    }

    if (multiSetting && targetType === 'category') {
        return 'Check adjustments across multiple settings before proceeding.';
    }

    const level = parentLevelLabel(targetType);
    if (editKind === 'color') {
        return `${affectedCountPhrase} have custom color settings that will be replaced by the ${level} setting.`;
    }
    if (editKind === 'printers' || editKind === 'receipt_printers') {
        return `${affectedCountPhrase} have custom printer settings that will be replaced by the ${level} setting.`;
    }
    return `${affectedCountPhrase} have custom settings that will be replaced by the ${level} setting.`;
}

function getOverrideAllHelpText(args: {
    editKind: OverrideEditKind;
    targetType: OverrideEntityType | undefined;
    displayColorTo?: string;
    posOnlineTo?: string;
    printerTo?: string;
    multiSetting?: boolean;
}): string {
    const { editKind, targetType, displayColorTo, posOnlineTo, printerTo, multiSetting } = args;

    if (targetType === 'item') {
        if (editKind === 'color') return 'Set a specific color for this item';
        if (editKind === 'visibility') return 'Set a specific service type for this item';
        return 'Apply this setting to this item';
    }

    if (multiSetting && targetType === 'category') {
        return 'Apply to all items';
    }

    if (editKind === 'color' && displayColorTo) {
        const hex = displayColorAsHex(displayColorTo);
        return `All items will use "${hex}"`;
    }
    if ((editKind === 'printers' || editKind === 'receipt_printers') && printerTo) {
        return `Use ${printerTo} for all items`;
    }
    if (editKind === 'visibility' && posOnlineTo) {
        return `All items will be set to ${posOnlineTo}`;
    }
    return 'Apply to all items';
}

function getKeepCurrentHelpText(args: {
    editKind: OverrideEditKind;
    targetType: OverrideEntityType | undefined;
    multiSetting?: boolean;
}): string {
    const { editKind, targetType, multiSetting } = args;

    if (targetType === 'item') {
        return 'Keep the menu-level setting for this item';
    }

    if (multiSetting && targetType === 'category') {
        return "Keep each item's settings";
    }

    if (editKind === 'color') {
        return 'Keep each individual colors';
    }
    if (editKind === 'printers' || editKind === 'receipt_printers') {
        return "Keep each item's printer";
    }
    if (editKind === 'visibility') {
        return "Keep each item's current service setting";
    }
    return "Keep each item's settings";
}

function getStep1PrimaryButtonText(editKind: OverrideEditKind, targetType: OverrideEntityType | undefined): string {
    if (targetType === 'item') {
        if (editKind === 'color') return 'Yes, set specific color';
        if (editKind === 'visibility') return 'Yes, set specific Service';
        return 'Yes, continue';
    }
    return 'Override All';
}

function getStep2BodyText(args: {
    editKind: OverrideEditKind;
    targetType: OverrideEntityType | undefined;
    affectedCountPhrase: string;
    multiSetting?: boolean;
}): string {
    const { editKind, targetType, affectedCountPhrase, multiSetting } = args;

    if (targetType === 'item') {
        if (editKind === 'color') {
            return 'Are you sure you want to override item color?';
        }
        if (editKind === 'visibility') {
            return 'Are you sure you want to override item service type?';
        }
        return 'Are you sure you want to override this item?';
    }

    if (multiSetting && targetType === 'category') {
        return 'Are you sure you want to apply overrides to these settings and items?';
    }

    return `Are you sure you want to override ${affectedCountPhrase}?`;
}

function getStep2AlertText(multiSetting?: boolean, targetType?: OverrideEntityType): string {
    if (multiSetting && targetType === 'category') {
        return 'Override Confirmation';
    }
    return 'Confirm Override';
}

function getSummaryRowLabel(editKind: OverrideEditKind, hasPosColor: boolean): string {
    if (editKind === 'color') {
        return hasPosColor ? 'POS Color' : 'Color';
    }
    if (editKind === 'printers' || editKind === 'receipt_printers') {
        return 'Printer';
    }
    return 'Service Type';
}

function pluralUnit(count: number, singular: string, plural = `${singular}s`): string {
    return `${count} ${count === 1 ? singular : plural}`;
}

function joinWithAnd(parts: string[]): string {
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function buildAffectedCountPhrase(args: {
    targetType?: OverrideEntityType;
    affectedRows: AffectedEntityRow[];
    fallbackCategoryCount: number;
    fallbackItemCount: number;
    fallbackTotalCount: number;
}): string {
    const { targetType, affectedRows, fallbackCategoryCount, fallbackItemCount, fallbackTotalCount } = args;

    if (targetType === 'item') {
        return pluralUnit(1, 'item');
    }

    if (affectedRows.length > 0) {
        const categoryCount = affectedRows.filter((row) => row.entityType === 'category').length;
        const itemCount = affectedRows.filter((row) => row.entityType === 'item').length;
        const parts: string[] = [];
        if (categoryCount > 0) parts.push(pluralUnit(categoryCount, 'category'));
        if (itemCount > 0) parts.push(pluralUnit(itemCount, 'item'));
        if (parts.length > 0) return joinWithAnd(parts);
        return pluralUnit(affectedRows.length, 'item');
    }

    if (targetType === 'menu') {
        const parts: string[] = [];
        if (fallbackCategoryCount > 0) parts.push(pluralUnit(fallbackCategoryCount, 'category'));
        if (fallbackItemCount > 0) parts.push(pluralUnit(fallbackItemCount, 'item'));
        if (parts.length > 0) return joinWithAnd(parts);
    }

    if (targetType === 'category') {
        return pluralUnit(fallbackTotalCount, 'item');
    }

    return pluralUnit(fallbackTotalCount, 'item');
}

interface AffectedDisplayGroup {
    categoryId: string;
    categoryName: string;
    items: AffectedEntityRow[];
}

function buildAffectedDisplayGroups(
    affected: AffectedEntityRow[],
    menusV2: any[],
    overrideSource?: OverrideSourceIds | null,
): AffectedDisplayGroup[] {
    const menu = overrideSource?.menuId
        ? menusV2.find((m: any) => m.id === overrideSource.menuId)
        : undefined;
    const groups = new Map<string, AffectedDisplayGroup>();

    const ensureGroup = (categoryId: string, categoryName: string) => {
        if (!groups.has(categoryId)) {
            groups.set(categoryId, { categoryId, categoryName, items: [] });
        }
        return groups.get(categoryId)!;
    };

    for (const row of affected) {
        if (row.entityType === 'category') {
            ensureGroup(row.id, row.name);
            continue;
        }

        let categoryId = `_item_${row.id}`;
        let categoryName = 'Items';
        if (menu) {
            const item = menu.items?.find((i: any) => i.id === row.id);
            const category = item
                ? menu.categories?.find((c: any) => c.id === item.category_id)
                : null;
            if (category) {
                categoryId = category.id;
                categoryName = category.name ?? 'Category';
            }
        }
        ensureGroup(categoryId, categoryName).items.push(row);
    }

    const ordered: AffectedDisplayGroup[] = [];
    if (menu?.categories?.length) {
        for (const category of menu.categories) {
            const group = groups.get(category.id);
            if (group) ordered.push(group);
        }
    }
    for (const group of groups.values()) {
        if (!ordered.includes(group)) ordered.push(group);
    }
    return ordered;
}

@inject("store") @observer
class MenuPreOverrideClass extends MobxComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { 
            step: 1,
            toggleAffectedItems: false,
            isConfirming: false,
        };
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.show && !prevProps.show) {
            this.setState({ step: 1, toggleAffectedItems: false, isConfirming: false });
        }
        if (!this.props.show && prevProps.show) {
            this.setState({ isConfirming: false });
        }
    }

    handleConfirm = async () => {
        if (this.state.isConfirming) return;
        this.setState({ isConfirming: true });
        try {
            await Promise.resolve(this.props.onConfirm?.());
        } finally {
            if (this.props.show) {
                this.setState({ isConfirming: false });
            }
        }
    };

    handleKeepCurrent = async () => {
        if (this.state.isConfirming) return;
        this.setState({ isConfirming: true });
        try {
            await Promise.resolve(this.props.onKeepCurrent?.());
        } finally {
            if (this.props.show) {
                this.setState({ isConfirming: false });
            }
        }
    };

    render() {
        const { show, onHide, onConfirm, onKeepCurrent, onShowSettings, title, targetEntity, overrideSource, changesSummary, editKind = 'visibility', affectedEntities, multiSetting = false } = this.props;
        const { step, toggleAffectedItems, isConfirming } = this.state;
        const entityLabel = targetEntity
            ? `${targetEntity.type.charAt(0).toUpperCase() + targetEntity.type.slice(1)}: ${targetEntity.name}`
            : step === 1 ? 'this entity' : 'the selected entity';

        const affected = filterAffectedRowsForKind(affectedEntities, editKind);
        const scopedSummary = filterChangesSummaryForKind(changesSummary, editKind);

        const disp = scopedSummary?.displayColor ?? scopedSummary?.color;
        const hasDisplayColor = !!(disp && (disp.from !== undefined || disp.to !== undefined));
        const hasPosColor = !!(scopedSummary?.posColor && (scopedSummary.posColor.from !== undefined || scopedSummary.posColor.to !== undefined));
        const hasPrinters = !!(scopedSummary?.printers && (scopedSummary.printers.from?.length || scopedSummary.printers.to?.length));
        const hasPosPrinters = !!(scopedSummary?.posPrinters && (scopedSummary.posPrinters.from?.length || scopedSummary.posPrinters.to?.length));
        const poPrev = scopedSummary?.posOnlinePreview;
        const hasPosOnlinePreview = !!(
            poPrev &&
            (poPrev.from !== undefined || poPrev.to !== undefined) &&
            poPrev.from !== poPrev.to
        );
        const hasServiceTypes = !!(scopedSummary?.serviceTypes && (scopedSummary.serviceTypes.from?.length || scopedSummary.serviceTypes.to?.length));
        const hasVisibilityAssignment = hasPosOnlinePreview || hasServiceTypes;

        const menusV2 = (this.injected.store.restaurant?.menus_v2 ?? []) as any[];
        const { categoryCount, itemCount, serviceLabels } = getCountsAndServices(menusV2, overrideSource ?? undefined, targetEntity?.type ?? 'menu');
        const targetCategoryCount =
            typeof targetEntity?.categoryCount === 'number' && targetEntity.categoryCount > 0
                ? targetEntity.categoryCount
                : 0;
        const targetItemCount =
            typeof targetEntity?.itemCount === 'number' && targetEntity.itemCount > 0
                ? targetEntity.itemCount
                : 0;
        const effectiveItemCount = Math.max(targetItemCount, itemCount);
        const effectiveCategoryCount = Math.max(targetCategoryCount, categoryCount);
        const combinedMenuDescendantCount = effectiveItemCount + effectiveCategoryCount;
        const fallbackDescendantCount = Math.max(effectiveItemCount, effectiveCategoryCount);
        // Prefer explicit affected rows (e.g. bulk-selected items) over menu-wide descendant totals.
        const affectedCountForHint =
            affected.length > 0
                ? affected.length
                : targetEntity?.type === 'menu'
                    ? combinedMenuDescendantCount
                    : targetEntity?.type === 'item'
                        ? 1
                        : fallbackDescendantCount;
        const affectedCountPhrase = buildAffectedCountPhrase({
            targetType: targetEntity?.type,
            affectedRows: affected,
            fallbackCategoryCount: effectiveCategoryCount,
            fallbackItemCount: effectiveItemCount,
            fallbackTotalCount: affectedCountForHint,
        });
        const affectedDisplayGroups = buildAffectedDisplayGroups(
            affected,
            menusV2,
            overrideSource ?? undefined,
        );
        const servicesText = serviceLabels.length > 0 ? serviceLabels.join(', ') : '—';

        const isItemLevel = targetEntity?.type === 'item';
        const printerToLabel =
            editKind === 'printers' || editKind === 'receipt_printers'
                ? (scopedSummary?.posPrinters?.to?.length
                    ? formatPosPrinterValuesForDisplay(scopedSummary!.posPrinters!.to).join(', ')
                    : scopedSummary?.printers?.to?.length
                        ? scopedSummary!.printers!.to!.join(', ')
                        : undefined)
                : undefined;

        const step1AlertText = getStep1AlertText(editKind, targetEntity?.type, multiSetting);
        const step1BodyText = getStep1BodyText({
            editKind,
            targetType: targetEntity?.type,
            affectedCountPhrase,
            multiSetting,
        });
        const overrideAllHelpText = getOverrideAllHelpText({
            editKind,
            targetType: targetEntity?.type,
            displayColorTo: disp?.to,
            posOnlineTo: poPrev?.to,
            printerTo: printerToLabel,
            multiSetting,
        });
        const keepCurrentHelpText = getKeepCurrentHelpText({
            editKind,
            targetType: targetEntity?.type,
            multiSetting,
        });
        const step2AlertText = getStep2AlertText(multiSetting, targetEntity?.type);
        const step2BodyText = getStep2BodyText({
            editKind,
            targetType: targetEntity?.type,
            affectedCountPhrase,
            multiSetting,
        });
        const step1PrimaryButtonText = getStep1PrimaryButtonText(editKind, targetEntity?.type);
        const summaryRowLabel = getSummaryRowLabel(editKind, hasPosColor);
        const hasChangeSummary =
            hasDisplayColor ||
            hasPosColor ||
            hasPrinters ||
            hasPosPrinters ||
            hasVisibilityAssignment;

        const modalTitle =
            title ||
            `${targetEntity?.name ?? 'Menu'} - ${getModalTitleSuffix(editKind, targetEntity?.type)}`;

        return (
            <QuickSettingsContainer className={show ? 'active' : ''}>
                {isConfirming && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(255, 255, 255, 0.75)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 102,
                        }}
                    >
                        <RotateLoader size={4} />
                    </div>
                )}
                <MenuColorHeaderWrapper>
                    <MenuColorHeaderTitleWrapper>
                        {modalTitle}
                    </MenuColorHeaderTitleWrapper>
                    <MenuItem onClick={isConfirming ? undefined : onHide}><IconClose /></MenuItem>
                </MenuColorHeaderWrapper>
                <MenuColorBodyWrapper style={{ minHeight: '50vh', overflowY: 'scroll', justifyContent: 'space-between' }}>
                    {step === 1 && (
                        <>
                        <div style={{ display: 'flex', gap: 24, flexDirection: 'column'}}>
                            <MenuStatusAlert variant='danger'>
                                <IconKioskWrapper variant='danger'>
                                    <IconAlertDanger />
                                </IconKioskWrapper>
                                <MenuStatusParagraph style={{ color: '#BC362F' }}>
                                    {step1AlertText}
                                </MenuStatusParagraph>
                            </MenuStatusAlert>
                            <MenuStatusParagraph>
                                {step1BodyText}
                            </MenuStatusParagraph>
                            <div style={{ display: 'flex', gap: 12, flexDirection: 'column'}}>
                                {hasChangeSummary && (
                                    <MenuStatusLabel>Change Summary</MenuStatusLabel>
                                )}
                                <MenuStatusAssignmentContainer>
                                    {hasDisplayColor && (
                                        <MenuStatusAssignmentList>
                                            {editKind !== 'color' && (
                                                <MenuStatusLabel>{summaryRowLabel}</MenuStatusLabel>
                                            )}
                                            {renderColorArrowPair(scopedSummary?.displayColor?.from, scopedSummary?.displayColor?.to)}
                                        </MenuStatusAssignmentList>
                                    )}
                                    {hasPosColor && (
                                        <MenuStatusAssignmentList>
                                            <MenuStatusLabel>POS Color</MenuStatusLabel>
                                            {renderColorArrowPair(scopedSummary!.posColor!.from, scopedSummary!.posColor!.to)}
                                        </MenuStatusAssignmentList>
                                    )}
                                    {hasPrinters && (
                                        <MenuStatusAssignmentList>
                                            <MenuStatusLabel>{summaryRowLabel}</MenuStatusLabel>
                                            {renderArrowPairLists(scopedSummary!.printers!.from ?? [], scopedSummary!.printers!.to ?? [])}
                                        </MenuStatusAssignmentList>
                                    )}
                                    {hasPosPrinters && (
                                        <MenuStatusAssignmentList>
                                            <MenuStatusLabel>{summaryRowLabel}</MenuStatusLabel>
                                            {renderPosPrinterArrowPairLists(scopedSummary!.posPrinters!.from, scopedSummary!.posPrinters!.to)}
                                        </MenuStatusAssignmentList>
                                    )}
                                    {hasVisibilityAssignment && editKind === 'visibility' && (
                                        <MenuStatusAssignmentList>
                                            <MenuStatusLabel>{summaryRowLabel}</MenuStatusLabel>
                                            {hasPosOnlinePreview ? (
                                                renderArrowPair(
                                                    scopedSummary!.posOnlinePreview!.from,
                                                    scopedSummary!.posOnlinePreview!.to
                                                )
                                            ) : (
                                                hasServiceTypes &&
                                                renderArrowPairLists(
                                                    scopedSummary!.serviceTypes!.from ?? [],
                                                    scopedSummary!.serviceTypes!.to ?? []
                                                )
                                            )}
                                        </MenuStatusAssignmentList>
                                    )}
                                </MenuStatusAssignmentContainer>

                                {affected.length > 0 && !isItemLevel && (
                                    <>
                                        <MenuStatusLabel 
                                            style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}
                                            onClick={() => this.setState({ toggleAffectedItems: !toggleAffectedItems })}
                                        >
                                            {/* {editKind === 'color' && 'Affected (custom display / POS colors)'}
                                            {editKind === 'visibility' && `Affected ${targetEntity?.type === 'menu' ? 'categories & items' : 'items'}`}
                                            {editKind === 'printers' && 'Affected (custom printer routing)'} */}
                                            Show Items Affected <span style={{ transform: toggleAffectedItems ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-flex' }}><IconArrowForward/></span>
                                        </MenuStatusLabel>
                                        { toggleAffectedItems && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        {affectedDisplayGroups.map((group) => (
                                            <div key={group.categoryId}>
                                                <MenuStatusLabel>{group.categoryName}</MenuStatusLabel>
                                                {group.items.length > 0 && (
                                                    <ul style={{ paddingLeft: 24, margin: '4px 0 0' }}>
                                                        {group.items.map((row) => (
                                                            <li key={`${row.entityType}-${row.id}`}>
                                                                <MenuStatusParagraph>
                                                                    {row.name}
                                                                </MenuStatusParagraph>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        ))}
                                        </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {onShowSettings && (
                                <MenuStatusShowSettingsContainer onClick={onShowSettings}>
                                    <MenuStatusShowSettingsText style={{ color: '#000' }}>Show settings</MenuStatusShowSettingsText>
                                    <IconArrowForward />
                                </MenuStatusShowSettingsContainer>
                            )}
                        </div>
                            {!isItemLevel && (
                            <MenuBodyFooter>
                               
                                <MenuStatusParagraph
                                    style={{ display: 'flex', gap: 8, alignItems: 'center'}}>
                                        <IconAlertDefault /> What do these options mean?</MenuStatusParagraph>
                                <ul style={{paddingLeft: '1rem'}}> 
                                    <li>
                                    <MenuStatusLabel style={{display: 'flex', gap: 4, flexWrap: 'wrap'}}>Override {isItemLevel ? '' : 'all'}:
                                            <MenuStatusParagraph>{overrideAllHelpText}</MenuStatusParagraph>
                                        </MenuStatusLabel>
                                    </li>
                                    {!isItemLevel && (
                                    <li>
                                        <MenuStatusLabel style={{display: 'flex', gap: 4, flexWrap: 'wrap'}}> Keep current:
                                            <MenuStatusParagraph>{keepCurrentHelpText}</MenuStatusParagraph>
                                        </MenuStatusLabel>
                                    </li>
                                    )}
                                </ul>     
                            </MenuBodyFooter>
                            )}
                        </>
                    )}

                    {step === 2 && (
                        <div style={{ display: 'flex', gap: 24, flexDirection: 'column', width: '100%'}}>
                            <MenuStatusAlert variant='danger'>
                                <IconKioskWrapper variant='danger'>
                                    <IconAlertDanger />
                                </IconKioskWrapper>
                                <MenuStatusAlertText variant='danger'>{step2AlertText}</MenuStatusAlertText>
                            </MenuStatusAlert>
                                <MenuStatusParagraph>
                                    {step2BodyText}
                                </MenuStatusParagraph>
                        </div>
                    )}
                </MenuColorBodyWrapper>
                <MenuColorFooterWrapper>
                    {step === 1 && (
                        <>
                            <ButtonV2 variant="default" onClick={onHide}>Cancel</ButtonV2>
                            {!isItemLevel && (
                                <ButtonV2 variant="default" disabled={isConfirming} onClick={() => { 
                                    void this.handleKeepCurrent();
                                }}>Keep Current</ButtonV2>
                            )}
                            <ButtonV2 disabled={isConfirming} onClick={() => this.setState({ step: 2 })}>{step1PrimaryButtonText}</ButtonV2>
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <ButtonV2 variant="default" disabled={isConfirming} onClick={onHide}>Cancel</ButtonV2>
                            <ButtonV2 disabled={isConfirming} onClick={() => { void this.handleConfirm(); }}>Yes</ButtonV2>
                        </>
                    )}
                </MenuColorFooterWrapper>
            </QuickSettingsContainer>
        );
    }
}
export const MenuPreOverride = withTranslation()(MenuPreOverrideClass);
